"use client";

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Save, Loader2, Camera } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import { useAppToast } from '@/lib/use-app-toast';
import { auditLog } from '@/lib/supabase/audit-logger';

interface ProfileForm {
  fullName: string;
  email: string;
  role: string;
  organization: string;
  phone: string;
  nid: string;
  department: string;
  designation: string;
}

export default function ProfileSettings() {
  const { language } = useLanguage();
  const [isSaving, setIsSaving] = useState(false);
  const { success } = useAppToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ProfileForm>({
    defaultValues: {
      fullName: 'Rafiqul Alam',
      email: 'r.alam@btrc.gov.bd',
      role: 'CISO',
      organization: 'Bangladesh Telecommunication Regulatory Commission',
      phone: '+880 1711-234567',
      nid: '19871234567890',
      department: 'Cyber Security Division',
      designation: 'Chief Information Security Officer',
    },
  });

  const onSubmit = async (data: ProfileForm) => {
    setIsSaving(true);
    // Backend integration: await supabase.from('profiles').update(data).eq('id', userId)
    await new Promise(resolve => setTimeout(resolve, 1200));
    await auditLog.profileUpdated();
    setIsSaving(false);
    success(t(language, 'settings_saved'), 'Profile information updated successfully');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Avatar Section */}
      <div className="cyber-card p-5">
        <h2 className={`text-sm font-semibold text-white mb-4 ${language === 'bn' ? 'font-bangla' : ''}`}>
          {t(language, 'settings_profile')}
        </h2>

        <div className="flex items-center gap-5 mb-6 pb-5 border-b border-cyber-border">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-cyber-blue/20 border-2 border-cyber-blue/30 flex items-center justify-center">
              <span className="text-2xl font-bold text-cyber-blue">RA</span>
            </div>
            <button
              type="button"
              className="absolute -bottom-1 -right-1 w-6 h-6 bg-cyber-blue rounded-full flex items-center justify-center hover:bg-cyber-blue/80 transition-colors"
            >
              <Camera size={11} className="text-white" />
            </button>
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Rafiqul Alam</p>
            <p className="text-xs text-cyber-muted font-mono">CISO · BTRC</p>
            <p className="text-[11px] text-cyber-blue mt-1 cursor-pointer hover:underline">Change photo</p>
          </div>
        </div>

        {/* Fields Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Name */}
          <div className="space-y-1.5">
            <label className={`text-xs font-medium text-cyber-muted ${language === 'bn' ? 'font-bangla' : ''}`}>
              {t(language, 'settings_profile_name')} <span className="text-cyber-red">*</span>
            </label>
            <input
              {...register('fullName', { required: 'Name is required' })}
              className={`w-full bg-[#131620] border rounded-lg px-3 py-2 text-sm text-cyber-text placeholder-cyber-subtle outline-none transition-colors font-mono ${
                errors.fullName ? 'border-cyber-red focus:border-cyber-red' : 'border-cyber-border focus:border-cyber-blue/50'
              }`}
            />
            {errors.fullName && <p className="text-[11px] text-cyber-red">{errors.fullName.message}</p>}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <label className={`text-xs font-medium text-cyber-muted ${language === 'bn' ? 'font-bangla' : ''}`}>
              {t(language, 'settings_profile_email')} <span className="text-cyber-red">*</span>
            </label>
            <input
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' }
              })}
              className={`w-full bg-[#131620] border rounded-lg px-3 py-2 text-sm text-cyber-text placeholder-cyber-subtle outline-none transition-colors font-mono ${
                errors.email ? 'border-cyber-red' : 'border-cyber-border focus:border-cyber-blue/50'
              }`}
            />
            {errors.email && <p className="text-[11px] text-cyber-red">{errors.email.message}</p>}
          </div>

          {/* Role */}
          <div className="space-y-1.5">
            <label className={`text-xs font-medium text-cyber-muted ${language === 'bn' ? 'font-bangla' : ''}`}>
              {t(language, 'settings_profile_role')}
            </label>
            <select
              {...register('role')}
              className="w-full bg-[#131620] border border-cyber-border rounded-lg px-3 py-2 text-sm text-cyber-text outline-none focus:border-cyber-blue/50 transition-colors cursor-pointer font-mono"
            >
              <option value="CISO">CISO — Chief Information Security Officer</option>
              <option value="SOC_ANALYST">SOC Analyst</option>
              <option value="IT_ADMIN">IT Administrator</option>
              <option value="COMPLIANCE">Compliance Officer</option>
              <option value="AUDITOR">Security Auditor</option>
            </select>
          </div>

          {/* Organization */}
          <div className="space-y-1.5">
            <label className={`text-xs font-medium text-cyber-muted ${language === 'bn' ? 'font-bangla' : ''}`}>
              {t(language, 'settings_profile_org')}
            </label>
            <input
              {...register('organization')}
              className="w-full bg-[#131620] border border-cyber-border rounded-lg px-3 py-2 text-sm text-cyber-text outline-none focus:border-cyber-blue/50 transition-colors font-mono"
            />
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <label className={`text-xs font-medium text-cyber-muted ${language === 'bn' ? 'font-bangla' : ''}`}>
              {t(language, 'settings_profile_phone')}
            </label>
            <input
              type="tel"
              {...register('phone')}
              className="w-full bg-[#131620] border border-cyber-border rounded-lg px-3 py-2 text-sm text-cyber-text outline-none focus:border-cyber-blue/50 transition-colors font-mono"
              placeholder="+880 17XX-XXXXXX"
            />
          </div>

          {/* NID */}
          <div className="space-y-1.5">
            <label className={`text-xs font-medium text-cyber-muted ${language === 'bn' ? 'font-bangla' : ''}`}>
              {t(language, 'settings_profile_nid')}
            </label>
            <p className={`text-[10px] text-cyber-subtle mb-1 ${language === 'bn' ? 'font-bangla' : ''}`}>
              Required for BD Compliance Act 2023 — stored encrypted
            </p>
            <input
              {...register('nid')}
              className="w-full bg-[#131620] border border-cyber-border rounded-lg px-3 py-2 text-sm text-cyber-text outline-none focus:border-cyber-blue/50 transition-colors font-mono"
              placeholder="NID or Passport Number"
            />
          </div>

          {/* Department */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-cyber-muted">Department</label>
            <input
              {...register('department')}
              className="w-full bg-[#131620] border border-cyber-border rounded-lg px-3 py-2 text-sm text-cyber-text outline-none focus:border-cyber-blue/50 transition-colors font-mono"
            />
          </div>

          {/* Designation */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-cyber-muted">Designation</label>
            <input
              {...register('designation')}
              className="w-full bg-[#131620] border border-cyber-border rounded-lg px-3 py-2 text-sm text-cyber-text outline-none focus:border-cyber-blue/50 transition-colors font-mono"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between">
        {isDirty && (
          <p className="text-[11px] text-yellow-400 font-mono flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full" />
            Unsaved changes
          </p>
        )}
        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            className="px-4 py-2 rounded-lg border border-cyber-border text-sm text-cyber-muted hover:bg-white/5 transition-all duration-150"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving || !isDirty}
            className={`
              flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium
              transition-all duration-150 active:scale-95
              ${isSaving || !isDirty
                ? 'bg-cyber-surface border border-cyber-border text-cyber-subtle cursor-not-allowed' :'bg-cyber-blue hover:bg-cyber-blue/80 text-white shadow-[0_0_15px_rgba(0,163,255,0.25)]'
              }
            `}
            style={{ minWidth: '120px' }}
          >
            {isSaving ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={14} />
                <span className={language === 'bn' ? 'font-bangla' : ''}>{t(language, 'settings_save')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}